import { normalizeBetaSource } from '../../utils/betaSources';
import {
    formatUserFactorModel,
    getEffectiveFactorDataModel,
    normalizeFactorDataModel,
    normalizeUserFactorModel,
} from '../../utils/factorDataOptions';
import {
    buildSelectedOverviewRefreshContextKey,
    normalizeIdioVolSource,
} from './dashboardUtils';
import { buildSelectedRiskOverviewScopeKey } from './riskOverviewResource';

// The selected Risk Overview scope is computed in two places from the same
// inputs: while rendering (the scope the visible panels must match) and by the
// explicit refresh command (the scope it requests). Both use these functions so
// a published snapshot always matches the scope the page expects.

export const POS_SIZING_PORTFOLIO_LABEL = 'pos-sizing';

const normalizeContextFactorDataModel = (factorModel, factorDataModelValue) => (
    normalizeFactorDataModel(normalizeUserFactorModel(factorModel), factorDataModelValue)
);

export function resolveActiveFactorSelection({
    acceptedFactorContext = null,
    selectedFactorModel,
    selectedAxiomaModel,
    selectedBarraModel,
} = {}) {
    return {
        activeSelectedFactorModel: acceptedFactorContext
            ? formatUserFactorModel(acceptedFactorContext.factorModel)
            : selectedFactorModel,
        activeRequestedFactorDataModel: acceptedFactorContext?.factorDataModel
            ?? getEffectiveFactorDataModel(
                selectedFactorModel,
                selectedAxiomaModel,
                selectedBarraModel,
            ),
    };
}

// The inputs buildPortfolioOverviewRiskContext reads, from Dashboard state
// (while rendering) or from its latest-value refs (in the refresh command).
export function buildOverviewRiskContextInputs({
    acceptedFactorContext = null,
    selectedFactorModel,
    selectedAxiomaModel,
    selectedBarraModel,
    factorDataModel,
    factorSnapshotDate,
    betaSource,
    idioVolSource,
    posSizingAutoSyncEnabled,
    posSizingSyncStatus,
} = {}) {
    return {
        ...resolveActiveFactorSelection({
            acceptedFactorContext,
            selectedFactorModel,
            selectedAxiomaModel,
            selectedBarraModel,
        }),
        factorDataModel,
        factorSnapshotDate,
        betaSource,
        idioVolSource,
        posSizingAutoSyncEnabled,
        posSizingDraftIsStale: Boolean(posSizingSyncStatus?.is_stale),
        posSizingSavedContext: posSizingSyncStatus?.saved_context ?? null,
    };
}

export function findSelectedOverviewPortfolio({
    portfolios,
    selectedId,
    selectedName,
} = {}) {
    if (!selectedId && !selectedName) {
        return null;
    }
    return portfolios?.find((portfolio) => (
        (selectedId != null && String(portfolio?.id) === String(selectedId))
        || portfolio?.value === selectedName
    )) ?? null;
}

const selectedPortfolioSignature = (portfolio) => JSON.stringify([
    portfolio?.id ?? null,
    portfolio?.lastRefreshedAt ?? null,
    portfolio?.compatibility ?? null,
]);

// A saved-portfolio list refresh matters to the selected Overview only when the
// selected portfolio appears, disappears, is re-saved or changes compatibility.
export function selectedOverviewPortfolioChanged({
    previousPortfolios,
    nextPortfolios,
    selectedId,
    selectedName,
} = {}) {
    return selectedPortfolioSignature(findSelectedOverviewPortfolio({
        portfolios: previousPortfolios,
        selectedId,
        selectedName,
    })) !== selectedPortfolioSignature(findSelectedOverviewPortfolio({
        portfolios: nextPortfolios,
        selectedId,
        selectedName,
    }));
}

export function isPosSizingOverviewSelection({
    portfolioId = null,
    portfolioName = '',
    portfolioType = null,
    posSizingPortfolioId = null,
} = {}) {
    if (
        portfolioId != null
        && posSizingPortfolioId != null
        && String(portfolioId) === String(posSizingPortfolioId)
    ) {
        return true;
    }
    if (portfolioType === 'wi') {
        return true;
    }
    return String(portfolioName || '').trim() === POS_SIZING_PORTFOLIO_LABEL;
}

export function buildPortfolioOverviewRiskContext(portfolioMeta, {
    activeSelectedFactorModel,
    activeRequestedFactorDataModel,
    factorDataModel,
    factorSnapshotDate,
    betaSource,
    idioVolSource,
    posSizingDraftIsStale,
    posSizingSavedContext,
} = {}) {
    const shouldUseSavedSnapshotContext =
        portfolioMeta?.type === 'wi' &&
        Boolean(posSizingDraftIsStale);

    if (!shouldUseSavedSnapshotContext) {
        return {
            factorModel: normalizeUserFactorModel(activeSelectedFactorModel),
            factorDataModel: normalizeContextFactorDataModel(
                activeSelectedFactorModel,
                factorDataModel ?? activeRequestedFactorDataModel,
            ),
            factorSnapshotDate: factorSnapshotDate ? String(factorSnapshotDate).trim() : null,
            betaSource: normalizeBetaSource(betaSource),
            idioVolSource: normalizeIdioVolSource(idioVolSource),
            usesSavedSnapshot: false,
        };
    }

    const savedContext = posSizingSavedContext ?? {};
    const normalizedFactorModel = normalizeUserFactorModel(
        savedContext?.factor_model ?? activeSelectedFactorModel,
    );

    return {
        factorModel: normalizedFactorModel,
        factorDataModel: normalizeContextFactorDataModel(
            normalizedFactorModel,
            savedContext?.factor_data_model,
        ),
        factorSnapshotDate:
            (savedContext?.factor_snapshot_date
                ? String(savedContext.factor_snapshot_date).trim()
                : null) ||
            (factorSnapshotDate ? String(factorSnapshotDate).trim() : null),
        betaSource: normalizeBetaSource(savedContext?.beta_source ?? betaSource),
        idioVolSource: normalizeIdioVolSource(
            savedContext?.idio_vol_source ?? idioVolSource,
        ),
        usesSavedSnapshot: true,
    };
}

// Returns null when there is no user or the selection is not a listed
// portfolio; otherwise the request target, its risk context and both keys.
export function resolveSelectedOverviewScope({
    userUid,
    portfolios,
    selectedId,
    selectedName,
    posSizingPortfolioId,
    watchlistVersion,
    posSizingVersion,
    invalidationVersion,
    riskContextInputs,
} = {}) {
    const portfolio = findSelectedOverviewPortfolio({ portfolios, selectedId, selectedName });
    if (!userUid || portfolio?.id == null) {
        return null;
    }
    const showingPosSizing = isPosSizingOverviewSelection({
        portfolioId: portfolio.id,
        portfolioName: portfolio.value ?? portfolio.name ?? selectedName,
        portfolioType: portfolio.type ?? null,
        posSizingPortfolioId,
    });
    const targetKey = showingPosSizing ? 'pos' : 'opt';
    const riskContext = buildPortfolioOverviewRiskContext(
        showingPosSizing ? { ...portfolio, type: 'wi' } : portfolio,
        riskContextInputs,
    );
    const refreshToken = portfolio.lastRefreshedAt ?? null;
    return {
        portfolio,
        portfolioId: portfolio.id,
        refreshToken,
        targetKey,
        riskContext,
        contextKey: buildSelectedOverviewRefreshContextKey({
            targetKey,
            watchlistVersion,
            posSizingVersion,
            invalidationVersion,
            riskContext,
        }),
        scopeKey: buildSelectedRiskOverviewScopeKey({
            userUid,
            portfolioId: portfolio.id,
            portfolioRefreshToken: refreshToken,
            watchlistVersion,
            posSizingVersion,
            invalidationVersion,
            targetKey,
            riskContext,
        }),
    };
}
